import React, { useRef, useState, useEffect } from 'react'
import { motion, useMotionValue, useTransform, useVelocity, useViewportScroll, useSpring } from 'framer-motion'
import { directusFileUrl } from 'src/helpers/directus'

interface ILogoMarqueeProps {
  imageIds?: string[]
  children?: React.ReactNode
  className?: string
  pyClassName?: string
  baseVelocity?: number
  slowOnHover?: boolean
  isSlowed?: boolean
  slowFactor?: number
}

// Helper to wrap values for seamless looping
const wrap = (min: number, max: number, v: number) => {
  const rangeSize = max - min
  return ((((v - min) % rangeSize) + rangeSize) % rangeSize) + min
}

const LogoMarquee: React.FC<ILogoMarqueeProps> = ({
  imageIds = [],
  children,
  className = '',
  pyClassName = 'py-0',
  baseVelocity = -1,
  slowOnHover = false,
  isSlowed = false,
  slowFactor = 0.25,
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const [contentWidth, setContentWidth] = useState(0)
  const speedFactorRef = useRef(1)

  // Measure content width for seamless wrapping
  useEffect(() => {
    const measure = () => {
      if (contentRef.current) {
        setContentWidth(contentRef.current.getBoundingClientRect().width)
      }
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [imageIds, children])

  const baseX = useMotionValue(0)
  const { scrollY } = useViewportScroll()
  const scrollVelocity = useVelocity(scrollY)

  // Smooth out velocity spikes
  const smoothVelocity = useSpring(scrollVelocity, {
    damping: 50,
    stiffness: 400
  })

  // Pixel-based X transformation
  const x = useTransform(baseX, (v) => {
    if (contentWidth === 0) return "0px"
    return `${wrap(-contentWidth, 0, v)}px`
  })

  const effectiveSlowed = isSlowed || (slowOnHover && isHovered)

  // Animation loop using requestAnimationFrame for smoothness
  useEffect(() => {
    let animationFrameId: number
    let lastTime = performance.now()

    const loop = (time: number) => {
      const delta = time - lastTime
      lastTime = time

      // Smoothly interpolate speed factor
      const targetFactor = effectiveSlowed ? slowFactor : 1
      speedFactorRef.current += (targetFactor - speedFactorRef.current) * 0.08

      // Get scroll velocity and scale it
      let currentVelocity = smoothVelocity.get() / 250

      // Combine base speed with scroll velocity and apply speed factor
      let speed = (baseVelocity - currentVelocity) * speedFactorRef.current

      // Normalize by frame time (~16.66ms)
      baseX.set(baseX.get() + speed * (delta / 16.666))

      animationFrameId = requestAnimationFrame(loop)
    }

    animationFrameId = requestAnimationFrame(loop)

    return () => cancelAnimationFrame(animationFrameId)
  }, [baseVelocity, smoothVelocity, baseX, effectiveSlowed, slowFactor])

  const renderItems = () => (
    <div className="flex items-center flex-shrink-0">
      {children ? children : imageIds.map((imageId, i) => (
        <div key={`${imageId}-${i}`} className="flex-shrink-0 px-6 md:px-12">
          <img
            src={directusFileUrl(imageId)}
            alt="Client logo"
            className="h-60 md:h-32 lg:h-48 w-auto opacity-85 dark:opacity-90 lg:hover:opacity-100 transition-opacity duration-300 dark:invert dark:brightness-110 select-none"
          />
        </div>
      ))}
    </div>
  )

  return (
    <div
      className={`w-full overflow-hidden py-0 bg-transparent select-none ${className}`}
      onMouseEnter={slowOnHover ? () => setIsHovered(true) : undefined}
      onMouseLeave={slowOnHover ? () => setIsHovered(false) : undefined}
    >
      <motion.div
        className="flex flex-nowrap will-change-transform w-max"
        style={{ x }}
      >
        <div ref={contentRef} className="flex flex-shrink-0">
          {renderItems()}
        </div>
        {/* Duplicates for seamless loop on ultra-wide screens with few items */}
        <div className="flex flex-shrink-0">{renderItems()}</div>
        <div className="flex flex-shrink-0">{renderItems()}</div>
        <div className="flex flex-shrink-0">{renderItems()}</div>
        <div className="flex flex-shrink-0">{renderItems()}</div>
        <div className="flex flex-shrink-0">{renderItems()}</div>
      </motion.div>
    </div>
  )
}

export default LogoMarquee
